import React, { Children, useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Container as RuiContainer } from 'roadie-ui'
import { BasicButton } from 'components/layout/buttons'
import { Helmet } from 'react-helmet'
import Page from 'rui/widgets/Page'
import Thin from 'components/layout/Thin'
import Panel from 'components/layout/Panel'
import styled from 'styled-components'
import media from 'styles/mixins/media'
import Gtm from 'util/gtm'
import { sendNativeMessage } from 'util/native'
import get from 'lodash/get'
import { addDriverCert, completeDriverOnboardingModule } from 'actions/profile'
import Input from 'components/form/Input'
import { Body2, Subtitle2 } from 'components/layout/text'
import uniq from 'lodash/uniq'
import uniqueId from 'lodash/uniqueId'
import Form, { baseError } from 'components/form'
import { TRAINING_CERTS } from 'util/constants'
import Player from '@vimeo/player'
import Icon from 'components/misc/Icon'
import { LocalizedRichText, LocalizedText, useLocalization } from 'localization'

const ElementTitle = styled(Body2)`
  width: 100%;
  background-color: ${(props) => props.theme.colors.primaryBase};
  color: ${(props) => props.theme.colors.white};
  text-transform: uppercase;
  font-weight: bold;
  padding: 0.5rem 1rem;
`

const Question = styled(Subtitle2)`
  margin-top: 1.2rem;
  margin-bottom: 0.8rem;
`

const Option = styled.div`
  padding: 0.4rem 1rem 1rem 1rem;
  background-color: ${(props) =>
    props.valueSelected
      ? props.isCorrect
        ? props.theme.colors.successLighter
        : props.wasChecked
          ? props.theme.colors.errorLighter
          : null
      : null};
`

const NavContainer = styled.div`
  padding-top: 2.4rem;
  border-top: 1px solid ${(props) => props.theme.colors.greyLight};
`

const Nav = styled.div`
  display: flex;
  justify-content: space-between;
  flex-shrink: 0;
`

const NavGap = styled.div`
  width: 8rem;
`

const WebviewPanel = styled(Panel)`
  height: 100vh;
`

const FullWidth = styled.div`
  width: 100%;
  margin-bottom: 1rem;
`

const WebPanel = styled(Panel)`
  height: calc(100vh - 20rem);

  ${media.mobile`
    height: 100vh;
  `}
`

const ModuleContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`

const StyledForm = styled(Form)`
  height: 100%;
  display: flex;
  flex-direction: column;
`

const SlideScroll = styled.div`
  overflow: scroll;
  overflow-x: hidden;
  flex-grow: 1;

  &::-webkit-scrollbar {
    display: none;
  }
`

const ModulePanel = (props) => {
  const { isWebview } = props
  const isMobile = useSelector((state) => state.config.mediaSize === 'mobile')
  const [height, setHeight] = useState(() =>
    isWebview ? window.innerHeight : isMobile ? `calc(${window.innerHeight}px - 6.4rem)` : undefined
  )

  const calcHeight = useCallback(() => {
    let newHeight

    if (isWebview) newHeight = window.innerHeight
    else if (isMobile) newHeight = `calc(${window.innerHeight}px - 6.4rem)`

    if (newHeight !== height) setHeight(newHeight)
  }, [height, isWebview, isMobile])

  useEffect(() => {
    window.addEventListener('resize', calcHeight)
    return () => {
      window.removeEventListener('resize', calcHeight)
    }
  })

  const Wrapper = isWebview ? WebviewPanel : WebPanel

  return (
    <Wrapper style={{ maxHeight: height }} left>
      {props.children}
    </Wrapper>
  )
}

export const Slide = (props) => {
  return <div>{props.children}</div>
}

const BaseModule = (props) => {
  const { certification, slides, congrats, navigation: _navigation, onFormData, keyPrefix = '' } = props
  const dispatch = useDispatch()
  const { t } = useLocalization()
  const [started, setStarted] = useState(false)
  const certs = useSelector((state) => get(state, 'trainingCertifications.delivery_certifications_drivers', []))
  const profile = useSelector((state) => state.profile)
  const vehicles = useSelector((state) => state.vehicles)
  const slideFormRef = React.createRef()
  const [error, setError] = useState(false)

  const startCert = useCallback(() => {
    if (!started && !certs.find((c) => c.name === certification)) {
      setStarted(true)
      sendNativeMessage({ type: 'START_CERTIFICATION', name: certification })
      Gtm.push({ event: `driver_cert_started_${certification}` })
    }
  }, [certs, started, certification])

  const completeCert = useCallback(() => {
    return dispatch(
      certification !== TRAINING_CERTS.ONBOARDING
        ? addDriverCert(certification)
        : completeDriverOnboardingModule(certification, profile.id, vehicles)
    ).then(() => {
      sendNativeMessage({ type: 'COMPLETE_CERTIFICATION', name: certification })
      Gtm.push({ event: `driver_cert_completed_${certification}` })
    })
  }, [dispatch, certification, profile, vehicles])

  const slideRef = useRef()

  const [slideIndex, setSlideIndex] = useState(0)

  const navigation = useMemo(() => {
    return _navigation?.(slideIndex)
  }, [_navigation, slideIndex])
  const handleBack = useCallback(() => {
    setSlideIndex((at) => at - 1)

    if (slideRef.current) {
      slideRef.current.scrollTop = 0
    }
  }, [slideRef])

  const handleData = useCallback(
    (data) => {
      let foundError = false
      let foundErrorField = null

      if (data) {
        for (const [field, value] of Object.entries(data)) {
          if (field.startsWith('quiz') && !field.endsWith('status') && value === '') {
            foundError = t('dweb_certifications_framework_select_answer_error')
            foundErrorField = field
          }

          if (field.startsWith('video') && value !== 'finished') {
            foundError = t('dweb_certifications_framework_finish_video_error')
          }

          if (field.startsWith('not_agreed') && value) {
            foundError = t('dweb_certifications_framework_check_all_boxes_error')
          }
        }
      }

      const isModuleComplete = foundError ? false : onFormData(data, slideIndex)
      return {
        error: foundError,
        errorField: foundErrorField,
        isModuleComplete,
      }
    },
    [onFormData, slideIndex, t]
  )

  const nextSlide = useCallback(() => {
    setSlideIndex((at) => {
      if (at === 0) {
        startCert()
      }
      return at + 1
    })

    if (slideRef.current) {
      slideRef.current.scrollTop = 0
    }

    if (error) {
      setError(false)
    }
  }, [error, startCert])

  const handleNext = useCallback(
    async (data) => {
      const { error: newError, errorField, isModuleComplete } = handleData(data)
      if (newError) {
        setError(newError)
        if (errorField && slideRef.current) {
          const errorElement = slideRef.current.querySelector(`[data-field="${errorField}"]`)
          errorElement?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
        }
        return baseError()
      } else {
        if (isModuleComplete) {
          try {
            await completeCert()
          } catch (_) {
            dispatch({
              type: 'PUSH_FLASH',
              flash: { type: 'error', message: t('dweb_certifications_framework_completion_error') },
            })
            return
          }
        }
        nextSlide()
        return Promise.resolve()
      }
    },
    [completeCert, dispatch, handleData, nextSlide, t]
  )

  const currentSlide = slides[slideIndex]

  const isLastSlide = slideIndex === slides.length - 1

  const isFirstSlide = slideIndex === 0

  if (currentSlide) {
    return (
      // Key prop is added to make ModuleContainer rerender whenever "Back" button is being hit
      <ModuleContainer key={`${keyPrefix}${slideIndex}`}>
        <StyledForm key={`${keyPrefix}form-${String(slideIndex)}`} ref={slideFormRef} onData={handleNext}>
          <SlideScroll id="slide" ref={slideRef}>
            {currentSlide}
          </SlideScroll>
          <NavContainer>
            {error && (
              <FullWidth>
                <Body2 align="center" color="errorBase">
                  {error}
                </Body2>
              </FullWidth>
            )}
            <Nav>
              {isFirstSlide ? (
                <BasicButton type="submit" mode="primary" palette="info">
                  <LocalizedText translationKey="dweb_common_get_started" />
                </BasicButton>
              ) : navigation ? (
                navigation
              ) : (
                <>
                  <NavGap />
                  <BasicButton type="button" mode="tertiary" disabled={isFirstSlide} onClick={handleBack}>
                    <LocalizedText translationKey="dweb_common_back" />
                  </BasicButton>
                  <NavGap />
                  <BasicButton type="submit" mode="primary" palette={isLastSlide ? 'success' : 'primary'}>
                    {isLastSlide ? 'Complete' : 'Next'}
                  </BasicButton>
                </>
              )}
            </Nav>
          </NavContainer>
        </StyledForm>
      </ModuleContainer>
    )
  } else {
    return (
      <RuiContainer.Position $position="relative" $minHeight="100%">
        {congrats}
      </RuiContainer.Position>
    )
  }
}

export const Module = (props) => {
  const { children, certification } = props

  const [congrats, slides] = useMemo(() => {
    const slides = Children.toArray(children)
    const congrats = slides.pop()

    return [congrats, slides]
  }, [children])
  const handleFormData = useCallback(
    (_, slideIndex) => {
      return slideIndex === slides.length - 1
    },
    [slides.length]
  )

  return <BaseModule certification={certification} slides={slides} congrats={congrats} onFormData={handleFormData} />
}

export const QuizModule = (props) => {
  const { children, certification, minimumScoreToPass } = props
  const resultSlide = children.resultSlide

  /* Used as a way to force the module to rerender completely. This essentially resets it */
  const [attempts, setAttempts] = useState(1)
  const [correctAnswers, setCorrectAnswers] = useState(0)
  const [quizCompleted, setQuizCompleted] = useState(false)
  const [passed, setPassed] = useState(false)

  const handleResetModule = useCallback(() => {
    setAttempts((attempt) => attempt + 1)
    setCorrectAnswers(0)
    setQuizCompleted(false)
    setPassed(false)
  }, [])

  const { slides, questions, agreements } = useMemo(() => {
    const slides = Children.toArray(children.slides.props.children)
    const questions = Children.toArray(children.questions.props.children)
    const agreements = Children.toArray(children.agreement?.props.children || [])
    return { slides, questions, agreements }
  }, [children])

  const allSlides = useMemo(() => {
    if (!quizCompleted || passed) {
      return [...slides, ...questions, ...agreements]
    }
    // if failed, show no agreements
    return [...slides, ...questions]
  }, [quizCompleted, passed, slides, questions, agreements])

  const congrats = useMemo(() => {
    return resultSlide({
      passed,
      numCorrectAnswers: correctAnswers,
      resetModule: handleResetModule,
    })
  }, [passed, correctAnswers, handleResetModule, resultSlide])

  const navigation = useCallback(
    (slideIndex) => {
      const totalSlides = slides.length + questions.length + (passed ? agreements.length : 0)
      const isLastQuizQuestion = slideIndex === slides.length + questions.length - 1
      const isLastSlide = slideIndex === totalSlides - 1

      if (slideIndex < slides.length) return null

      return (
        <BasicButton type="submit" mode="primary" palette="primary">
          {isLastQuizQuestion ? 'Complete Quiz' : isLastSlide && passed ? 'Complete' : 'Next'}
        </BasicButton>
      )
    },
    [slides.length, questions.length, agreements.length, passed]
  )

  const handleFormData = useCallback(
    (data, slideIndex) => {
      let newCorrectAnswers = 0

      if (data) {
        for (const [field, value] of Object.entries(data)) {
          if (field.startsWith('quiz') && field.endsWith('status') && value === true) {
            newCorrectAnswers++
          }
        }
      }

      const updatedCorrect = correctAnswers + newCorrectAnswers
      setCorrectAnswers(updatedCorrect)

      // If user reached last quiz question, determine pass/fail
      const isLastQuestion = slideIndex === slides.length + questions.length - 1
      if (isLastQuestion) {
        const passedNow = updatedCorrect >= minimumScoreToPass
        setPassed(passedNow)
        setQuizCompleted(true)
      }

      const isLastSlide = slideIndex === allSlides.length - 1
      return isLastSlide ? updatedCorrect >= minimumScoreToPass : false
    },
    [correctAnswers, minimumScoreToPass, slides.length, questions.length, allSlides.length]
  )

  return (
    <BaseModule
      key={`module-${certification}-${attempts}`}
      keyPrefix={`${certification}-${attempts}-`}
      certification={certification}
      slides={allSlides}
      congrats={quizCompleted ? congrats : null}
      navigation={navigation}
      onFormData={handleFormData}
    />
  )
}

const VideoContainer = styled.div`
  position: relative;

  & > iframe {
    visibility: ${(props) => (!props.showVideoInBackground && !props.playing ? 'hidden' : 'visible')};
  }
`

const VideoPlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: ${(props) => (!props.showVideoInBackground ? 'flex-start' : 'center')};
  justify-content: center;
`

function isVisibleInsideContainer(element, container) {
  const elementTop = element.offsetTop
  const elementBottom = elementTop + element.clientHeight
  const containerTop = container.scrollTop
  const containerBottom = containerTop + container.clientHeight
  const minPartVisible = (containerBottom - elementTop) / (elementBottom - elementTop) >= 0.3
  const elementIsVisible =
    (elementTop >= containerTop && elementBottom <= containerBottom) ||
    (elementTop <= containerTop && elementBottom >= containerBottom) ||
    (elementTop < containerTop && containerTop < elementBottom) ||
    (elementTop < containerBottom && containerBottom < elementBottom)

  return elementIsVisible && minPartVisible
}

// Usage example:
//
// <Slide>
//   <h1>Some slide</h1>
//   <Video src="/some-video.mp4" />
// </Slide>
//
export const Video = (props) => {
  // TODO: Video settings changed in WEB-2524 due to iOS 18 video issues, when Apple fixes it we can revert this
  const { autoplayWhenVisible = true, showVideoInBackground = true } = props
  const [loaded, setLoaded] = useState(false)
  const [name] = useState(() => `video-${uniqueId()}`)
  const [finished, setFinished] = useState(false)
  const videoRef = React.createRef()
  const elementRef = React.createRef()
  const [player, setPlayer] = useState(null)
  const [height, setHeight] = useState(() => props.height || '500')

  const handlePlay = useCallback(async () => {
    if (!loaded && player) {
      if (!showVideoInBackground) {
        await player.requestFullscreen()
      }
      await player.play()
      setLoaded(true)
    }
  }, [loaded, player, showVideoInBackground])

  const handleScroll = useCallback(() => {
    if (elementRef.current) {
      const visible = isVisibleInsideContainer(elementRef.current, document.getElementById('slide'))
      if (visible) {
        handlePlay()
      }
    }
  }, [elementRef, handlePlay])

  const handleVideoEnded = useCallback(() => {
    setFinished(true)
  }, [])

  useEffect(() => {
    const element = videoRef.current

    if (element) {
      const vimeo = new Player(element)
      setPlayer(vimeo)
      const handleVideoEvent = (data) => {
        if (data.percent >= 0.9) {
          handleVideoEnded()
        }
      }
      vimeo.on('timeupdate', handleVideoEvent)
      vimeo.on('ended', handleVideoEvent)

      if (autoplayWhenVisible) {
        const slideEl = document.getElementById('slide')
        slideEl.addEventListener('scroll', handleScroll)
        handleScroll()
      }
    }

    return () => {
      if (autoplayWhenVisible) {
        const slideEl = document.getElementById('slide')
        if (slideEl) {
          slideEl.removeEventListener('scroll', handleScroll)
        }
      }
      if (player) {
        player.off('timeupdate')
        player.off('ended')
      }
    }
  }, [autoplayWhenVisible, videoRef, handleScroll, handleVideoEnded, player])

  useEffect(() => {
    if (player !== null) {
      const wPromise = player.getVideoWidth()
      const hPromise = player.getVideoHeight()

      Promise.all([wPromise, hPromise]).then(([videoWidth, videoHeight]) => {
        if (videoRef.current !== null) {
          const containerWidth = videoRef.current.parentElement.offsetWidth
          const aspectRatio = videoWidth / videoHeight
          const newHeight = containerWidth / aspectRatio
          setHeight(newHeight)
        }
      })
    }
  }, [player, videoRef])

  return (
    <VideoContainer ref={elementRef} showVideoInBackground={showVideoInBackground} playing={loaded}>
      <iframe
        ref={videoRef}
        title={name}
        src={props.src}
        width={props.width || '100%'}
        height={height}
        allowFullScreen
      />
      <Input.Hidden name={name} value={finished ? 'finished' : ''} />
      {loaded ? null : (
        <VideoPlayContainer showVideoInBackground={showVideoInBackground} onClick={handlePlay}>
          {/* TODO: Video settings changed in WEB-2524 due to iOS 18 new video permissions */}
          <Icon height="15rem" width="10rem" symbol="video-play-circled" />
        </VideoPlayContainer>
      )}
    </VideoContainer>
  )
}

const BaseQuiz = (props) => {
  const { name, options } = props
  const { t } = useLocalization()
  const numOfAttempts = props.numOfAttempts ?? options.length + 1
  const [checked, setChecked] = useState([])
  const handleChange = (event) => {
    if (checked.length >= numOfAttempts) {
      return
    }

    setChecked(uniq([event.target.value, ...checked]))

    const option = options.find((option) => option.value === event.target.value)
    props?.onChange(Boolean(option?.isCorrect))
  }
  const isValueSelected = Boolean(checked.length)
  return (
    <Input.RadioGroup name={name} onChange={handleChange} defaultValue={checked[0]}>
      {(radioElementProps) => (
        <>
          {options.map((option) => (
            <Option
              key={`option-${name}-${option.value}`}
              isCorrect={Boolean(option.isCorrect)}
              wasChecked={checked.includes(option.value)}
              valueSelected={isValueSelected}
            >
              <Input.RadioElement
                {...radioElementProps}
                handleChange={checked.length >= numOfAttempts ? null : radioElementProps.handleChange}
                key={option.value}
                tight={true}
                value={String(option.value)}
                label={option.textKey ? t(option.textKey) : option.text}
              />
            </Option>
          ))}
        </>
      )}
    </Input.RadioGroup>
  )
}

// Usage example:
//
// <Slide>
//   <h1>Some slide</h1>
//   <Quiz
//     questionKey="dweb_certifications_onboarding_quiz_q1_question"
//     descriptionKey="dweb_certifications_onboarding_quiz_q1_description"
//     options={[
//       { textKey: 'dweb_certifications_onboarding_quiz_q1_option_1', value: '1' },
//       { textKey: 'dweb_certifications_onboarding_quiz_q1_option_2', value: '2', isCorrect: true }
//     ]}
//   />
// </Slide>
//
export const Quiz = (props) => {
  const { question, questionKey, description, descriptionKey, options } = props
  const { t } = useLocalization()
  const localizedQuestion = questionKey ? t(questionKey) : question
  const localizedDescription = descriptionKey ? t(descriptionKey) : description
  const [name] = useState(() => `quiz-${uniqueId()}`)
  const [isValueSelected, setIsValueSelected] = useState(false)
  const handleChange = () => {
    setIsValueSelected(true)
  }
  return (
    <div data-field={name}>
      <ElementTitle>
        <LocalizedText translationKey="dweb_certifications_framework_quiz" />
      </ElementTitle>
      <Question>{localizedQuestion}</Question>
      <BaseQuiz name={name} options={options} onChange={handleChange} />
      {isValueSelected && localizedDescription && <Body2 weight="bold">{localizedDescription}</Body2>}
    </div>
  )
}

// Usage example:
//
// <Slide>
//   <h1>Some slide</h1>
//   <RequiredQuiz
//     questionKey="dweb_certifications_onboarding_quiz_q1_question"
//     descriptionKey="dweb_certifications_onboarding_quiz_q1_description"
//     options={[
//       { textKey: 'dweb_certifications_onboarding_quiz_q1_option_1', value: '1' },
//       { textKey: 'dweb_certifications_onboarding_quiz_q1_option_2', value: '2', isCorrect: true }
//     ]}
//   />
// </Slide>
//
export const RequiredQuiz = (props) => {
  const { question, questionKey, options, description, descriptionKey, questionNumber, totalQuestions } = props
  const { t } = useLocalization()
  const localizedQuestion = questionKey ? t(questionKey) : question
  const localizedDescription = descriptionKey ? t(descriptionKey) : description
  const [name] = useState(() => `quiz-${uniqueId()}`)
  const [isAnswerCorrect, setIsAnswerCorrect] = useState(null)
  const handleChange = (isCorrect) => {
    if (isAnswerCorrect === null) {
      setIsAnswerCorrect(isCorrect)
    }
  }
  return (
    <div data-field={name}>
      <ElementTitle>
        <LocalizedText translationKey="dweb_common_question_of" params={{ questionNumber, totalQuestions }} />
      </ElementTitle>
      <Question>{localizedQuestion}</Question>
      <Input.Hidden name={`${name}-status`} value={isAnswerCorrect} />
      <BaseQuiz name={name} options={options} onChange={handleChange} numOfAttempts={1} />
      {isAnswerCorrect !== null ? (
        <>
          <br />
          <Body2 weight="bold">
            <LocalizedText
              translationKey={
                isAnswerCorrect
                  ? 'dweb_certifications_framework_correct_answer'
                  : 'dweb_certifications_framework_incorrect_answer'
              }
            />
          </Body2>
          {localizedDescription ? (
            <>
              <br />
              <Body2>{localizedDescription}</Body2>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

export const Agree = (props) => {
  const { t } = useLocalization()
  const [agreed, setAgreed] = useState(false)
  const label = props.toKey ? (
    t(props.toKey)
  ) : props.toRichKey ? (
    <LocalizedRichText translationKey={props.toRichKey} params={props.params} />
  ) : (
    props.to
  )
  const fieldName =
    props.value || props.toKey || props.toRichKey || (Array.isArray(props.to) ? props.to.join('') : props.to)
  const fieldValue = typeof label === 'string' ? label : fieldName

  const handleAgree = useCallback((event) => {
    setAgreed(event.target.checked)
  }, [])

  return (
    <>
      <Input.Checkbox label={label} onChange={handleAgree} />
      {agreed ? null : <Input.Hidden type="hidden" name={`not_agreed_${fieldName}`} value={fieldValue} />}
    </>
  )
}

export const Container = (props) => {
  const isWebview = useSelector((state) => state.config.isWebview)
  // TODO using a reselect selector such as isWebviewSelector
  // has the opposite effect (it causes a recursive re-render loop)
  // Look into a way to fix this. See:
  // * https://react-redux.js.org/next/api/hooks#useselector-examples
  // * https://react-redux.js.org/api/hooks#using-memoizing-selectors

  if (isWebview) {
    return (
      <div>
        <Helmet>
          <title>{props.pageTitle || props.title || 'Training'}</title>
        </Helmet>
        <ModulePanel isWebview={isWebview}>{props.children}</ModulePanel>
      </div>
    )
  } else {
    return (
      <Page>
        <Helmet>
          <title>{props.pageTitle || props.title || 'Training'}</title>
        </Helmet>
        <Thin>
          <ModulePanel isWebview={isWebview}>{props.children}</ModulePanel>
        </Thin>
      </Page>
    )
  }
}
